import { useEffect, useState } from 'react';
import { useTranslation } from '../../i18n';
import { formatDuration } from '../../lib/contest';

/**
 * Server-authoritative countdown.
 *
 * The remaining time is computed from the SERVER's clock (offset by the request
 * round trip), not from the device clock, so a wrong system time on the device
 * cannot make the contest look open when it is closed. Nothing here grants or
 * denies access — the Worker does that — so this is purely a display.
 */
export function Countdown({
  label,
  targetMs,
  serverTime,
  fetchedAtMs,
  onExpire,
}: {
  label: string;
  targetMs: number;
  serverTime: string;
  fetchedAtMs: number;
  onExpire?: () => void;
}) {
  const { t } = useTranslation();
  const offset = Date.parse(serverTime) - fetchedAtMs;
  const [, setTick] = useState(0);

  useEffect(() => {
    const id = window.setInterval(() => setTick((value) => value + 1), 1000);
    return () => window.clearInterval(id);
  }, []);

  const remaining = targetMs - (Date.now() + (Number.isFinite(offset) ? offset : 0));

  useEffect(() => {
    if (remaining <= 0) onExpire?.();
    // Only fire on the transition, not on every render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [remaining <= 0]);

  return (
    <div className="flex flex-col gap-0.5">
      <span className="text-xs font-medium uppercase tracking-wide text-muted">{label}</span>
      <span className="font-mono text-lg font-semibold text-fg tabular-nums" aria-live="polite">
        {remaining > 0 ? formatDuration(remaining) : t('contest.countdown.past')}
      </span>
    </div>
  );
}
